// Platforms tab: bookSettings.platforms entries (platform, store link) with
// per-language Instructions / Description HTML snippets.
import React, { useState } from "react"
import Button from "aldoo-ra/Button"
import Typography from "aldoo-ra/Typography"
import { DevicePhoneMobileIcon } from "@heroicons/react/24/outline"
import { TextInput, Select } from "../../../AdminShared/ui"
import { DeleteIconButton, HTMLSnippetEditor } from "./shared"
import useProductEditorTexts from "aldoo-ra/CMSModels/StoreProduct/useProductEditorTexts"
import HelpHint from "aldoo-ra/HelpCenter/InPlaceHelp"

const platformIdOf = (entry) => (typeof entry.platform === "object" ? entry.platform?._id : entry.platform)

export default function PlatformsTab({ editor, platforms }) {
  const t = useProductEditorTexts()
  const { read, set, language } = editor
  const [htmlEditor, setHtmlEditor] = useState(null) // { idx, field }
  const entries = read("bookSettings.platforms", true) || []
  const setEntries = (next) => set("bookSettings.platforms", next, true)
  const update = (idx, patch) => setEntries(entries.map((e, i) => (i === idx ? { ...e, ...patch } : e)))

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <Typography component="h4" variant="admin_h4" className="my-0 inline-flex items-center gap-1.5">{t`Supported Platforms`}<HelpHint helpKey="vendor.product.setup#platforms" /></Typography>
        <Button variant="admin_primary" className="px-4" disabled={platforms.length === 0} onClick={() => setEntries([...entries, { platform: "", storeLink: "" }])} data-testid="add-platform">
          <DevicePhoneMobileIcon className="w-4 h-4" /> {t`Add Platform`}
        </Button>
      </div>
      <p className="text-sm text-admin_text_secondary my-0">{t`Select platforms where this product is available. Each entry has a product-specific download link.`}</p>
      {platforms.length === 0 && <p className="text-sm text-admin_warning my-0">{t`No platforms defined yet. Add platforms in %s from the catalog first.`.split("%s")[0]}<strong>{t`Platforms`}</strong>{t`No platforms defined yet. Add platforms in %s from the catalog first.`.split("%s")[1]}</p>}
      {entries.length === 0 && <p className="text-sm text-admin_text_secondary my-0">{t`No platforms added to this product yet.`}</p>}
      <div className="flex flex-col gap-3">
        {entries.map((entry, idx) => {
          const platformId = platformIdOf(entry)
          const selected = platforms.find((p) => String(p._id) === String(platformId))
          return (
            <div key={idx} className="p-3 border border-admin_bordered rounded-md flex flex-col gap-3" data-testid="platform-row">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 shrink-0 flex items-center justify-center border border-dashed border-admin_bordered rounded overflow-hidden">
                  {selected?.image ? <img src={selected.image} alt={selected.name} className="w-full h-full object-contain" /> : <DevicePhoneMobileIcon className="w-6 h-6 text-admin_text_secondary/60" />}
                </div>
                <Select value={platformId || ""} onChange={(v) => update(idx, { platform: v })} placeholder={t`Select platform…`} options={platforms.map((p) => ({ value: p._id, label: p.name }))} className="min-w-[180px]" searchEnabled={false} />
                <TextInput value={entry.storeLink || ""} onChange={(v) => update(idx, { storeLink: v })} placeholder={t`Store Link — https://apps.apple.com/app/...`} className="flex-1" />
                <DeleteIconButton onClick={() => setEntries(entries.filter((_, i) => i !== idx))} />
              </div>
              <div className="flex gap-2">
                <Button variant="admin_secondary" className="h-[30px] text-xs px-3" onClick={() => setHtmlEditor({ idx, field: "instructions" })}>{t("Instructions [%s]", language)}{entry.instructions?.[language] ? " ✓" : ""}</Button>
                <Button variant="admin_secondary" className="h-[30px] text-xs px-3" onClick={() => setHtmlEditor({ idx, field: "description" })}>{t("Description [%s]", language)}{entry.description?.[language] ? " ✓" : ""}</Button>
              </div>
            </div>
          )
        })}
      </div>
      {htmlEditor && (
        <HTMLSnippetEditor
          open
          onClose={() => setHtmlEditor(null)}
          value={entries[htmlEditor.idx]?.[htmlEditor.field]?.[language] ?? ""}
          onChange={(value) => {
            update(htmlEditor.idx, { [htmlEditor.field]: { ...(entries[htmlEditor.idx]?.[htmlEditor.field] || {}), [language]: value } })
            setHtmlEditor(null)
          }}
        />
      )}
    </div>
  )
}
